import { AgGridReact } from 'ag-grid-react';
import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { DateTime } from 'luxon';
import { ColDef, RowGroupOpenedEvent } from 'ag-grid-community';
import MainFrame from '../../components/MainFrame/mainFrame';
import styles from './confirmedRequestListScreenPage.module.css';
import mainStyles from '../main.module.css';
import usePageTitle from '../../hooks/title.hook';

import 'ag-grid-enterprise';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import Button from '../../components/Button/button';
import DatePickerJp from '../../components/DatePickerJp';
import { ReserveManagementItem, ReserveManagementList, ReserveManagementSearchForm } from '../../services/http/reserveManagement.api';
import { useSearchReserveManagementApi } from '../../hooks/api/reserveManagement.hook';
import DetailCellRenderer from './detailCellRenderer';
import AlertCloseModal, { alertModalInitialState } from './alertCloseModal';
import BaseTable from '../../components/BaseTable';
import Breadcrumb from '../../components/Breadcrumb';
import { useDocumentSearchForReserveNoApi } from '../../hooks/api/documentWarehousing.hook';
import ExportLabelComponent from './exportLabelComponent';
import Formatter from '../../utils/formatters';
import { ApiError } from '../../services/http';
import { useMessageModal } from '../../hooks/modal.hook';
import AGUtils from '../../utils/ag-grid.utils';
import useEffectOnce from '../../hooks/useEffectOnce.hook';
import { useCreateLogApi } from '../../hooks/api/log.hook';
import { LogControlName, LogFormName } from '../../utils/log.utils';
import PageTitle from '../../utils/pageTitles';
import { DeliveryType } from '../../services/http/documentWarehousing.api';
import SharedAutoComplete from '../../components/SharedAutoComplete/SharedAutoComplete';

/**
 * 確定依頼一覧ページ
 */
function Component() {
  usePageTitle(PageTitle.ConfirmedRequestList);
  const openMessageModal = useMessageModal();
  const { request: createLog } = useCreateLogApi();

  const gridRef = useRef<AgGridReact<ReserveManagementList>>(null);

  // States
  const [fromDate, setFromDate] = useState<DateTime | null>(DateTime.local().minus({ month: 1 }));
  const [toDate, setToDate] = useState<DateTime | null>(null);
  const [searchForm, setSearchForm] = useState<ReserveManagementSearchForm>({});
  const [reserveManagementList, setReserveManagementList] = useState<ReserveManagementList[]>([]);
  const [labelExportDisplay, setLabelExportDisplay] = useState(false);
  const [modal, setModal] = useState(alertModalInitialState);
  const [selectedReserveNos, setSelectedReserveNos] = useState<ReserveManagementItem[]>([]);

  // API's
  const searchReserveManagementApi = useSearchReserveManagementApi();
  const searchForReserveNoApi = useDocumentSearchForReserveNoApi();

  useEffect(() => {
    if (labelExportDisplay) {
      document.title = '保管品ラベル発行選択';
      return;
    }
    document.title = '確定済依頼一覧';
  }, [labelExportDisplay]);

  // Methods
  const resetForm = () => {
    setFromDate(null);
    setToDate(null);
    setSearchForm({});
  };

  // Callbacks
  const onSearchClick = useCallback(async () => {
    if (searchForm.requestType === '') {
      delete searchForm.requestType;
    }

    try {
      const getReserveManagementList = await searchReserveManagementApi.request(searchForm);

      if (getReserveManagementList.length > 2000) {
        setModal({
          open: true,
          text: ` 検索結果が2000件を超えているため、\n
              2001件目以降が表示されていません。\n
              検索条件を更に絞り込んで再度検索してください。`,
          onClose: () => setModal({ ...modal, open: false }),
        });
      }
      setReserveManagementList(getReserveManagementList.slice(0, 2000));
    } catch (e) {
      openMessageModal((e as ApiError)?.message);
    }
  }, [searchForm, modal, searchReserveManagementApi]);

  // Initial States
  useEffect(() => {
    setSearchForm((_searchForm) => ({ ..._searchForm, dueDate_From: fromDate ? Formatter.fromDateTimeToString(fromDate, Formatter.defaultFullDateFormat) : undefined }));
  }, [fromDate]);

  // Memos
  const requestTypes = useMemo(() => [
    { name: '入庫', type: '1' },
    { name: '出庫', type: '2' },
    { name: '廃棄', type: '3' },
    { name: 'リモートスキャン', type: '4' },
  ], []);

  const colDef = useMemo((): ColDef<ReserveManagementList>[] => [
    {
      colId: '0',
      headerName: '',
      suppressColumnsToolPanel: true,
      field: '',
      minWidth: 50,
      width: 50,
      headerCheckboxSelection: true,
      checkboxSelection: true,
      lockPosition: true,
      pinned: 'left',
    },
    {
      field: 'reserveNo',
      headerName: '',
      lockPosition: true,
      suppressColumnsToolPanel: true,
      cellRenderer: 'agGroupCellRenderer',
      cellClass: 'add-border-right',
      maxWidth: 30,
      minWidth: 30,
      cellStyle: { padding: '0', margin: '0' },
      pinned: 'left',
    },
    AGUtils.colAutoIncrement('no'),
    AGUtils.colDefault('reserveNo', '依頼番号', 110),
    AGUtils.colDefault('type', '依頼内容', 110),
    AGUtils.colDate('schDate', '予定日', true, {}, Formatter.defaultDateFormat),
    AGUtils.colDefault('sumWorkQty', '件数', 110),
    AGUtils.colDefault('name', '集配先', 130),
    {
      field: 'deliveryType',
      headerName: '集配方法',
      resizable: true,
      sortable: true,
      width: 130,
      cellClass: 'textFormat',
      valueGetter: ({ data }) => {
        if (data?.type === '廃棄' || data?.type === 'リモートスキャン') return '';
        const deliveryType = data?.deliveryType;
        if (deliveryType === '') return '';
        switch (Number(deliveryType)) {
          case DeliveryType.TeradaDelivery:
            return '寺田便';
          case DeliveryType.Courier:
            return '宅配便';
          default:
            return '';
        }
      },
    },
    {
      field: 'postNo',
      headerName: '住所',
      resizable: true,
      sortable: true,
      cellClass: 'textFormat',
      valueGetter: ({ data }) => [data?.postNo, data?.address1, data?.address2, data?.address3].join(', '),
      width: 200,
    },
    AGUtils.colUsername('staffName', '担当者名'),
    AGUtils.colDefault('phone', '電話番号', 150),
    {
      field: 'trdReserveManagementCd2',
      headerName: '廃棄処理報告書発行',
      resizable: true,
      sortable: true,
      cellClass: 'textFormat',
      valueGetter: ({ data }) => {
        const value = data?.trdReserveManagementCd2;
        switch (value) {
          case '0':
            return '要';
          case '1':
            return '不要';
          default:
            return '';
        }
      },
      width: 170,
    },
    AGUtils.colDefault('trdReserveNotes', '寺田倉庫への連絡事項', 200),
    AGUtils.colDefault('customerNotes', 'お客様メモ', 200),
    AGUtils.colDefault('createdBy', '依頼者', 110),
    AGUtils.colDate('createDatetime', '依頼日時'),
  ], []);

  const detailCellRenderer = (e: { data: ReserveManagementItem, pinned: string }) => <DetailCellRenderer pinned={e.pinned} reserveNo={e.data.reserveNo} type={e.data.type} />;

  const onRowGroupOpened = (params: RowGroupOpenedEvent<ReserveManagementList>) => {
    if (params.node.expanded) {
      params.api.forEachNode((node) => {
        if (
          node.expanded
            && node.id !== params.node.id
        ) {
          node.setExpanded(false);
        }
      });
    }
  };

  const handleLabelGenerate = (nos: ReserveManagementItem[]) => {
    const sortedNos: ReserveManagementItem[] = [];
    gridRef.current?.api?.forEachNodeAfterFilterAndSort((node) => {
      const no = nos.find(({ reserveNo }) => node.data?.reserveNo === reserveNo);
      if (no != null) {
        sortedNos.push(no);
      }
    });
    setLabelExportDisplay(true);
    try {
      searchForReserveNoApi.request(sortedNos);
    } catch (e) {
      openMessageModal((e as ApiError)?.message);
    }
  };

  const applyPinnedColumns = useCallback(() => {
    if (gridRef.current) {
      gridRef.current.columnApi.applyColumnState({
        state: [
          { colId: '0', pinned: 'left' },
          { colId: 'reserveNo', pinned: 'left' },
        ],
        applyOrder: true,
      });
    }
  }, [gridRef]);

  useEffect(() => {
    if (!labelExportDisplay) searchForReserveNoApi.setData(null);
  }, [labelExportDisplay]);

  const hasSelected = selectedReserveNos.length > 0;

  useEffectOnce(() => {
    createLog(LogFormName.ConfirmedRequestListScreen, LogControlName.Show);
  });

  const requestTypeOptions = [{ text: '', value: '' }, ...requestTypes.map((item) => ({ text: item.name, value: item.type }))];

  return (
    <MainFrame borderBox>
      <div className={styles.mainFrame}>
        <Breadcrumb crumbs={[{ label: document.title }]} />
        <div className={styles.mainFrameBody}>
          <AlertCloseModal open={modal.open} text={modal.text} onClose={modal.onClose} />
          <div className={styles['mainframe-leftsection']}>
            <header>
              検索条件
            </header>
            <div>
              <label>依頼内容</label>
              <div className={mainStyles['w-100']}>
                <SharedAutoComplete
                  value={requestTypes.find((item) => item.type === searchForm.requestType)?.name || ''}
                  options={requestTypeOptions}
                  onSelect={({ value }) => setSearchForm({ ...searchForm, requestType: value })}
                  onDeselect={() => setSearchForm({ ...searchForm, requestType: '' })}
                  disablePortal
                />
              </div>
            </div>

            <div>
              <label>予定日</label>
            </div>
            <div className={styles['date-field']}>
              <label>FROM</label>
              <DatePickerJp
                parentDivClassName={styles['w-100']}
                inputClassName={styles['w-100']}
                value={fromDate}
                onChange={(newValue) => {
                  setSearchForm({ ...searchForm, dueDate_From: newValue ? Formatter.fromDateTimeToString(newValue, Formatter.defaultFullDateFormat) : undefined });
                  setFromDate(newValue);
                }}
              />
              {' '}
            </div>
            <div className={styles['date-field']}>
              <label>TO</label>
              <DatePickerJp
                parentDivClassName={styles['w-100']}
                inputClassName={styles['w-100']}
                value={toDate}
                onChange={(newValue) => {
                  setSearchForm({ ...searchForm, dueDate_To: newValue ? Formatter.fromDateTimeToString(newValue, Formatter.defaultFullDateFormat) : undefined });
                  setToDate(newValue);
                }}
              />
            </div>
            <footer>
              <Button
                className={mainStyles['ml-10']}
                size="smaller"
                color="lighterGray"
                onClick={() => resetForm()}
              >
                条件リセット
              </Button>
              <Button
                className={mainStyles['mr-10']}
                size="smaller"
                onClick={onSearchClick}
                loading={searchReserveManagementApi.loading}
              >
                検索
              </Button>
            </footer>
          </div>
          <div className={styles['mainframe-rightsection']}>
            <BaseTable<ReserveManagementList>
              tableRef={gridRef}
              formName="confirmedRequestTable"
              rowData={reserveManagementList}
              columnDefs={colDef}
              onSelectionChanged={(e) => {
                setSelectedReserveNos(e.api.getSelectedRows().map((d) => ({
                  reserveNo: d.reserveNo,
                  type: d.type,
                })));
              }}
              suppressRowClickSelection
              rowSelection="multiple"
              masterDetail
              detailCellRenderer={detailCellRenderer}
              detailRowAutoHeight
              onRowGroupOpened={onRowGroupOpened}
              embedFullWidthRows
              sideBar
              useColPin
              createLogOnDownloadTableData={LogFormName.ConfirmedRequestListScreen}
              onColumnSettingsApplied={applyPinnedColumns}
              getRowId={(params) => params.data.reserveNo}
              exportFeatureName={PageTitle.ConfirmedRequestList}
              exportSheetName={PageTitle.ConfirmedRequestList}
              exportExcludeColIds={['0', 'reserveNo']}
            />
            {labelExportDisplay && (
              <ExportLabelComponent
                data={searchForReserveNoApi.data}
                display={labelExportDisplay}
                setModalDisplay={setLabelExportDisplay}
                tableName="confirmedRequestExportLabelComponent"
              />
            )}
            <div className={styles['mainframe-rightsection-footer']}>
              <Button
                className={styles.exportLabeButton}
                size="smaller"
                disabled={hasSelected === false}
                onClick={() => handleLabelGenerate(selectedReserveNos)}
              >
                保管品ラベルを発行する
              </Button>
            </div>
          </div>
        </div>
      </div>
    </MainFrame>
  );
}

export default Component;
